import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import ChargerStationCardList from '../../components/chargerStationCardList/ChargerStationCardList';
import ChargerStationDetails from '../../components/chargerStationDetails/ChargerStationDetails';
import HeaderSiteCharge from '../../components/header/headerSiteCharge/HeaderSiteCharge';
import SwipeToRefresh from '../../components/SwipeToRefresh/SwipeToRefresh';
import { useBrandContext } from '../../context/BrandContext';
import { useLocationContext } from '../../context/LocationContext';
import { useToast } from '../../context/ToastModalContext';
import { useToastStore } from '../../hooks/useToastStore';
import {
  getChargerSiteById,
  getFile,
} from '../../services/chargerSitesServices';
import { getChargerStations } from '../../services/chargerStationService';
import carHeaderBackground from '../../assets/carHeaderBackground.svg';
import { ReactComponent as LoaderSecondary } from '../../assets/SpinnerBlack.svg';

import './Charger.css';

// Intervalo da atualização automática da lista de conectores
const CONNECTORS_REFRESH_INTERVAL_MS = 15 * 1000;

const Charger: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [loadingTotalHeader, setLoadingTotalHeader] = useState(false);
  const [loadingLogo, setLoadingLogo] = useState(false);
  const [stations, setStations] = useState([]);
  const [chargerSite, setChargerSite] = useState<any>();
  const [stationDetails, setStationDetails] = useState<any>([]);
  const [csBgFile, setCsBgFile] = useState<any>('');
  const [csLogoFile, setCsLogoFile] = useState<any>('');
  const isFetchingConnectorsRef = useRef(false);
  const { id } = useParams();
  const { showToast } = useToast();
  const locationContext = useLocationContext();
  const brandContext = useBrandContext();

  const navigate = useNavigate();

  const {
    showHelpToast,
    showDirectionsToast,
    showStartTransactionToast,
    showNoCreditToast,
    showChargerUnavailableToast,
  } = useToastStore();

  const [siteData, setSiteData] = useState({ name: '', address: '' });

  useEffect(() => {
    fetchChargerSite();
  }, []);

  const fetchChargerSite = async () => {
    setLoading(true);
    setLoadingTotalHeader(true);
    try {
      const chargerSite = await getChargerSiteById(id!);
      setSiteData({
        name: chargerSite.csName,
        address: chargerSite.csFormattedAddress,
      });
      setChargerSite(chargerSite);
      if (!chargerSite.csBgFileId && !brandContext.appBgFileId) {
        setCsBgFile(carHeaderBackground);
      } else if (brandContext.appBgFileId) {
        const csBgFile = await getFile(brandContext.appBgFileId);
        setCsBgFile(csBgFile);
      }
      if (
        !(chargerSite && (chargerSite.csBgFileId || chargerSite.csLogoFileId))
      ) {
        setLoadingTotalHeader(false);
      }
    } catch (error: any) {
      showToast({
        title: `Falha`,
        messageModal: `Erro ao buscar site de carregamento`,
        textEnterButton: 'Tente novamente',
        allowButtonClosed: true,
        handleEnterButton: () => fetchChargerSite(),
        closeButtonText: 'Fechar',
      });
      if (error?.message !== 'Network Error' && error?.message !== 'Page not visible') {
        console.error('Erro ao buscar sites de carregamento', error);
      }
      setLoadingTotalHeader(false);
    }
    setLoading(false);
  };

  // silent: atualização automática — sem spinner (a lista não pisca nem perde a rolagem) e sem
  // toast de erro; numa falha fica a lista anterior e o próximo ciclo tenta de novo
  const fetchSiteConnectors = async (silent = false) => {
    if (silent && (isFetchingConnectorsRef.current || document.hidden)) return;
    isFetchingConnectorsRef.current = true;
    if (!silent) setLoading(true);
    try {
      const responseStations = await getChargerStations(id!);
      const sortedStations = responseStations.sort((a: any, b: any) => {
        const aLabel = (a.name || a.chargePointId || '').toString();
        const bLabel = (b.name || b.chargePointId || '').toString();
        if (
          a.cpStatus.toLowerCase() === 'online' &&
          b.cpStatus.toLowerCase() !== 'online'
        )
          return -1;
        if (
          a.cpStatus.toLowerCase() !== 'online' &&
          b.cpStatus.toLowerCase() === 'online'
        )
          return 1;
        return aLabel.localeCompare(bLabel);
      });
      setStations(sortedStations);
    } catch (error) {
      isFetchingConnectorsRef.current = false;
      if (silent) return;
      showToast({
        title: `Falha`,
        messageModal: `Erro ao buscar estações de carregamento`,
        textEnterButton: 'Tente novamente',
        allowButtonClosed: true,
        handleEnterButton: () => fetchSiteConnectors(),
        closeButtonText: 'Fechar',
      });
      console.error('Erro ao buscar sites de carregamento', error);
    }
    isFetchingConnectorsRef.current = false;
    if (!silent) setLoading(false);
  };

  useEffect(() => {
    if (!chargerSite) return;

    try {
      getStationDetails();
    } catch (error: any) {
      const errorCode = error?.message || 'ERR_UNKNOWN';
      showToast({
        title: 'Erro ao carregar detalhes do local',
        messageModal: `Não foi possível exibir as informações deste local. Tente novamente ou entre em contato com o suporte. [${errorCode}]`,
        textEnterButton: 'Tentar novamente',
        allowButtonClosed: true,
        handleEnterButton: () => fetchChargerSite(),
        closeButtonText: 'Fechar',
        onClose: () => navigate(`/${brandContext.brandName}`),
      });
    }

    const fetchChargerBgFile = async (fileId: string) => {
      setLoading(true);
      try {
        const csBgFile = await getFile(fileId);
        if (csBgFile && csBgFile.fileData) {
          setCsBgFile(csBgFile.fileData);
        } else if (brandContext.appBgFileId) {
          setCsBgFile(brandContext.appBgFileId);
        } else {
          setCsBgFile(carHeaderBackground);
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error('Erro ao buscar sites de carregamento', error);
      }
    };

    if (chargerSite.csBgFileId) {
      fetchChargerBgFile(chargerSite.csBgFileId);
    } else if (brandContext.appBgFileId) {
      fetchChargerBgFile(brandContext.appBgFileId);
    }

    if (chargerSite.csLogoFileId) {
      const fetchSiteConnectors = async () => {
        setLoadingLogo(true);
        try {
          const csLogoFile = await getFile(chargerSite.csLogoFileId);
          setCsLogoFile(csLogoFile?.fileData);
          setLoadingLogo(false);
        } catch (error) {
          setLoadingLogo(false);
          setCsLogoFile(brandContext.appLogoFile);
          console.error('Erro ao buscar sites de carregamento', error);
        }
      };

      fetchSiteConnectors();
    }

    setLoadingTotalHeader(false);
  }, [chargerSite]);

  useEffect(() => {
    fetchSiteConnectors();
    return () => {
      setCsBgFile('');
    };
  }, []);

  useEffect(() => {
    const intervalId = setInterval(
      () => fetchSiteConnectors(true),
      CONNECTORS_REFRESH_INTERVAL_MS
    );
    // Aba em segundo plano pula os ciclos; ao voltar, atualiza na hora
    const handleVisibilityChange = () => {
      if (!document.hidden) fetchSiteConnectors(true);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const formatOperationHours = (site: any = {}) => {
    const { csPrivate, isOpen, csAvailability = '[]' } = site;
    return `${csPrivate ? 'Privado' : 'Público'} / ${
      isOpen ? 'Aberto' : 'Fechado'
    } *`;
  };

  const getStationDetails = () => {
    const siteLocation: any = chargerSite?.csLocation?.coordinates
      ? {
          lat: chargerSite.csLocation.coordinates[1],
          lng: chargerSite.csLocation.coordinates[0],
        }
      : null;

    const coordinates = chargerSite?.csLocation?.coordinates;
    if (chargerSite?.csLocation != null && coordinates == null) {
      throw new Error('ERR_LOCATION_COORDINATES');
    }

    let hoursData: any[] = [];
    try {
      hoursData = JSON.parse(chargerSite?.csAvailability || '[]');
    } catch {
      throw new Error('ERR_AVAILABILITY_DATA');
    }

    setStationDetails({
      name: chargerSite?.csName,
      addressDistance: siteLocation
        ? locationContext.getUserDistance(siteLocation, 'km', 1)
        : null,
      address: chargerSite?.csFormattedAddress,
      parkingInfo: chargerSite?.csFreeParking
        ? 'Estacionamento: gratuito'
        : 'Estacionamento: pago',
      operationHours: formatOperationHours(chargerSite),
      cmcType: chargerSite?.cmcType,
      csPrivate: chargerSite?.csPrivate,
      lat: coordinates?.[1],
      long: coordinates?.[0],
      isOcpi: chargerSite?.isOcpi,
      hoursData,
      isOpen: chargerSite?.isOpen,
    });
  };

  return (
    <div
      style={
        showHelpToast ||
        showDirectionsToast ||
        showStartTransactionToast ||
        showNoCreditToast ||
        showChargerUnavailableToast
          ? {
              height: '100vh',
              width: '100vw',
              overflow: 'hidden',
              position: 'fixed',
              top: 0,
              left: 0,
            }
          : {
              position: 'relative',
              display: 'contents',
            }
      }
    >
      {!loadingTotalHeader && (
        <div style={{ flexShrink: 0 }}>
          <HeaderSiteCharge
            isLoadingLogo={loadingLogo}
            bgColor={chargerSite?.csBgColor}
            backgroundImageUrl={csBgFile}
            logo={csLogoFile}
            showActionButtons={true}
            backNavigation={() => navigate(`/${brandContext.brandName}`)}
            closeNavigation={() => navigate(`/${brandContext.brandName}`)}
          />
        </div>
      )}
      <SwipeToRefresh
        onSwipeDown={() => {
          fetchSiteConnectors();
        }}
      >
        <div className='app'>
          {Object.keys(stationDetails).length > 0 && (
            <ChargerStationDetails details={stationDetails} />
          )}

          {stations.length > 0 && !loading ? (
            <ChargerStationCardList chargers={stations} siteData={siteData} />
          ) : (
            loading && <LoaderSecondary />
          )}
        </div>
      </SwipeToRefresh>
    </div>
  );
};

export default Charger;
